/**
 * API 统一入口
 * 按模块分组，所有方法返回 Promise<T>，T 为业务数据类型
 * 后端响应已被 request.ts 拦截器自动拆包（ApiResponse.data）
 */
import http from './request';
import type { Partner, PartnerVO, PartnerProfileVO, PartnerAccountInfoVO, ChangePasswordDTO } from '@/types/partner';
import type { DashboardStats, PolicyAnnouncement, MarketActivity } from '@/types/dashboard';
import type { Certification, TechCertificationVO, CapabilityMatrixVO, CaseLibraryVO, EngineerProfileVO, ComplianceCertVO, CertificationChecklistVO, CertificationMaterialVO, MaterialItem, FileUploadResult, PaginatedResponse, PartnerAgreementVO, AgreementStatsVO, AgreementSignDTO, SlaStandardVO, SlaSubscriptionVO, QualityAuditVO, UpgradeConditionsVO, UpgradeApplicationVO, CertCatalogVO, AgentCertificateVO } from '@/types/certification';
import type { CertificationProgressVO } from '@/types/certification';
import type { NavTreeNode } from '@/types/common';
import type { ProductItem, ServerConfigGroup } from '@/types/quotation';
import type { LoginLogVO, ResetPasswordDTO } from '@/types/settings';
import type { HealthScoreVO, MdfRequestVO } from '@/types/health-mdf';
import {
  navTreeMockData,
  projectsNavTree,
  performanceNavTree,
  resourcesNavTree,
  quotationNavTree,
} from '@/mock/nav-data';

// ============================================================
// 认证模块
// ============================================================

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: {
    username: string;
    displayName: string;
    avatar?: string;
    /** 合作伙伴 ID（登录用户绑定） */
    partnerId?: string;
  };
  /** 是否需要强制修改密码（首次登录） */
  mustChangePassword?: boolean;
  /** 登录后重定向地址 */
  redirectUrl?: string;
}

export interface PasswordChangeResultVO {
  success: boolean;
  redirectUrl?: string;
}

export const authApi = {
  /** 登录 POST /api/auth/login */
  login: (data: LoginRequest) =>
    http.post<unknown, LoginResponse>('/auth/login', data),

  /** 登出 POST /api/auth/logout */
  logout: () =>
    http.post<unknown, boolean>('/auth/logout'),

  /** 获取当前用户/合作伙伴的基本信息 GET /api/partner/info */
  getProfile: () =>
    http.get<unknown, PartnerVO>('/partner/info'),

  /** 获取当前账号登录日志 GET /api/auth/login-logs */
  getLoginLogs: () =>
    http.get<unknown, LoginLogVO[]>('/auth/login-logs'),

  /** 验证手机号是否已注册（忘记密码用）POST /api/auth/forgot-password/verify-phone */
  verifyForgotPasswordPhone: (phone: string) =>
    http.post<unknown, { exists: boolean; partnerId?: string }>('/auth/forgot-password/verify-phone', { phone }),

  /** 重置密码（忘记密码流程）POST /api/auth/forgot-password/reset */
  resetForgotPassword: (data: { phone: string; captcha: string; smsCode: string; newPassword: string }) =>
    http.post<unknown, boolean>('/auth/forgot-password/reset', data),
};

// ============================================================
// 合作伙伴模块
// ============================================================

export const partnerApi = {
  /** 获取合作伙伴可编辑档案信息 GET /api/partner/profile */
  getPartnerInfo: () =>
    http.get<unknown, PartnerProfileVO>('/partner/profile'),

  /** 获取合作伙伴基础信息（含认证状态、代理等级等）GET /api/partner/info */
  getPartnerBaseInfo: () =>
    http.get<unknown, PartnerVO>('/partner/info'),

  /** 修改合作伙伴档案信息 PUT /api/partner/profile */
  updatePartnerProfile: (data: PartnerProfileVO) =>
    http.put<unknown, PartnerProfileVO>('/partner/profile', data),

  /** 修改登录密码 PUT /api/partner/password */
  changePassword: (data: ChangePasswordDTO) =>
    http.put<unknown, PasswordChangeResultVO>('/partner/password', data),

  /** 获取账号信息 GET /api/partner/account-info */
  getAccountInfo: () =>
    http.get<unknown, PartnerAccountInfoVO>('/partner/account-info'),

  /** 获取仪表盘统计数据 GET /api/dashboard/stats */
  getDashboardStats: () =>
    http.get<unknown, DashboardStats>('/dashboard/stats'),

  /** 获取认证资质列表 GET /api/certification/list */
  getCertifications: (category?: string) =>
    http.get<unknown, Certification[]>('/certification/list', {
      params: category ? { category } : undefined,
    }),

  /** 获取代理证书数据 GET /api/certification/agent-certificate（资质资料列表首行） */
  getAgentCertificate: () =>
    http.get<unknown, AgentCertificateVO>('/certification/agent-certificate'),

  /** 续期认证资质 POST /api/certification/renew */
  renewCertification: (certId: string) =>
    http.post<unknown, { success: boolean; certId: string }>(
      '/certification/renew',
      { certId },
    ),

  /** 提交合作伙伴申请 POST /api/partner/apply（无需认证）
   *  成功返回 { id, status: 0 }
   *  业务错误码:
   *    3001 — 申请正在审核中
   *    3003 — 已是合作伙伴，无需重复申请
   */
  submitApplication: (data: {
    companyName: string;
    province: string;
    city: string;
    contactEmail: string;
    contactPhone: string;
    cooperationType: string;
    businessScope?: string;
    companyScale?: string;
    contactName?: string;
  }) =>
    http.post<unknown, { id: string; status: number }>('/partner/apply', data),
};

// ============================================================
// 认证进度模块
// ============================================================

export const certificationApi = {
  /** 获取认证进度 GET /api/certification/progress */
  progress: () =>
    http.get<unknown, CertificationProgressVO>('/certification/progress'),

  /** 获取申请资料清单 GET /api/certification/checklist */
  getChecklist: () =>
    http.get<unknown, CertificationChecklistVO>('/certification/checklist'),

  /** 上传单个材料（先 MinIO 上传拿 objectName，再调用此接口记录到认证材料）POST /api/certification/materials/upload */
  uploadMaterial: (params: {
    materialType: string;
    materialName: string;
    fileName: string;
    fileUrl: string;
    fileSize?: number;
  }) =>
    http.post<unknown, CertificationMaterialVO>('/certification/materials/upload', null, {
      params,
    }),

  /** 提交申请材料 POST /api/certification/materials/submit */
  submitMaterials: () =>
    http.post<unknown, boolean>('/certification/materials/submit', { items: [] }),

  /** 删除已上传的认证材料 DELETE /api/certification/materials/{id} */
  deleteMaterial: (id: string) =>
    http.delete<unknown, boolean>(`/certification/materials/${encodeURIComponent(id)}`),

  /** 获取协议列表 GET /api/certification/agreements */
  getAgreements: () =>
    http.get<unknown, PartnerAgreementVO[]>('/certification/agreements'),

  /** 获取协议统计 GET /api/certification/agreements/stats */
  getAgreementStats: () =>
    http.get<unknown, AgreementStatsVO>('/certification/agreements/stats'),

  /** 下载协议数据（前端合成PDF）GET /api/certification/agreement/{id}/download */
  downloadAgreement: (id: string) =>
    http.get<unknown, Record<string, any>>(`/certification/agreement/${encodeURIComponent(id)}/download`),

  /** 签署合作协议 POST /api/certification/agreement/sign */
  signAgreement: (dto: AgreementSignDTO) =>
    http.post<unknown, boolean>('/certification/agreement/sign', dto),

  /** 提交已签署的协议审核 POST /api/certification/agreements/submit */
  submitAgreements: () =>
    http.post<unknown, boolean>('/certification/agreements/submit'),
};

// ============================================================
// 文件管理模块（MinIO）
// ============================================================

/** 联盟信息 VO（GET /api/alliance/info） */
export interface AllianceInfoVO {
  id: string;
  allianceName: string;
  description: string;
  logo: string;
  memberCount: number;
  foundedDate: string;
  contactPerson: string;
  contactInfo: string;
  rules: string;
  benefits: string;
  status: string;
  createTime: string;
}

/** 联盟 API */
export const allianceApi = {
  /** 联盟基本信息 GET /api/alliance/info */
  getInfo: () => http.get<unknown, AllianceInfoVO | null>('/alliance/info'),
  /** 联盟成员列表 GET /api/alliance/members */
  getMembers: (params?: { page?: number; pageSize?: number }) =>
    http.get<unknown, unknown>('/alliance/members', { params }),
  /** 联盟活动列表（复用首页市场活动接口）GET /api/activities */
  getActivities: () => http.get<unknown, unknown[]>('/activities'),
  /** 立即报名活动 POST /api/activities/register */
  registerActivity: (data: { activityId: string; contactName?: string; contactPhone?: string; remark?: string }) =>
    http.post<unknown, unknown>('/activities/register', data),
  /** 编辑我的报名信息 POST /api/activities/update-registration（需重新审核） */
  updateRegistration: (data: { id: string; contactName?: string; contactPhone?: string; remark?: string }) =>
    http.post<unknown, unknown>('/activities/update-registration', data),
  /** 我的报名记录 GET /api/activities/my-registrations */
  getMyRegistrations: () => http.get<unknown, unknown[]>('/activities/my-registrations'),
  /** 撤回报名 POST /api/activities/cancel */
  cancelRegistration: (id: string) =>
    http.post<unknown, boolean>('/activities/cancel', { id }),

  /* 论坛 */
  /** 帖子列表 GET /api/alliance/posts */
  getPosts: (params?: { page?: number; pageSize?: number; scope?: 'all' | 'mine'; tag?: string }) =>
    http.get<unknown, unknown>('/alliance/posts', { params }),
  /** 我的帖子 GET /api/alliance/my/posts */
  getMyPosts: (params?: { page?: number; pageSize?: number; tag?: string }) =>
    http.get<unknown, unknown>('/alliance/my/posts', { params }),
  /** 帖子详情 GET /api/alliance/posts/{id} */
  getPostDetail: (id: string) => http.get<unknown, unknown>(`/alliance/posts/${id}`),
  /** 发帖 / 更新 POST /api/alliance/posts */
  savePost: (data: { id?: string; title: string; content: string; tags?: string; status?: string }) =>
    http.post<unknown, unknown>('/alliance/posts', data),
  /** 删除帖子 DELETE /api/alliance/posts/{id} */
  deletePost: (id: string) => http.delete<unknown, boolean>(`/alliance/posts/${id}`),
  /** 点赞 / 取消点赞 POST /api/alliance/posts/{id}/like，返回 {liked, likeCount} 绝对值 */
  toggleLike: (id: string) => http.post<unknown, { liked: boolean; likeCount: number }>(`/alliance/posts/${id}/like`),
  /** 发表评论 POST /api/alliance/comments */
  addComment: (data: { postId: string; content: string; parentId?: string; replyToId?: string }) =>
    http.post<unknown, unknown>('/alliance/comments', data),
  /** 删除评论 DELETE /api/alliance/comments/{id} */
  deleteComment: (id: string) => http.delete<unknown, boolean>(`/alliance/comments/${id}`),
  /** 升级进度 GET /api/alliance/grade-progress */
  getGradeProgress: () => http.get<unknown, unknown>('/alliance/grade-progress'),
  /** 我的帖子统计 GET /api/alliance/my-unread（用于论坛 tab 红色气泡） */
  getMyUnread: () => http.get<unknown, { totalComments: number; totalLikes: number }>('/alliance/my-unread'),
};

export const filesApi = {
  /** 上传单个文件到 MinIO（multipart/form-data）POST /api/files/upload */
  upload: (file: File, dir: string = 'material') => {
    const formData = new FormData();
    formData.append('file', file);
    return http.post<unknown, FileUploadResult>('/files/upload', formData, {
      params: { dir },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },

  /** 删除 MinIO 文件 DELETE /api/files/delete */
  delete: (objectName: string) =>
    http.delete<unknown, boolean>('/files/delete', {
      params: { objectName },
    }),

  /** 获取文件预签名访问 URL GET /api/files/url */
  getUrl: (objectName: string) =>
    http.get<unknown, string>('/files/url', {
      params: { objectName },
    }),

  /** 多文件批量上传 POST /api/files/batch-upload */
  batchUpload: (files: File[], dir: string = 'material') => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return http.post<unknown, FileUploadResult[]>('/files/batch-upload', formData, {
      params: { dir },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
};

// ============================================================
// 服务能力模块（技术认证、能力矩阵、案例库、工程师、合规）
// ============================================================

export const serviceApi = {
  /** 获取技术认证列表（分页）GET /api/service/tech-certifications */
  getTechCertifications: (params: {
    page?: number;
    pageSize?: number;
    filters?: Record<string, unknown>;
    keyword?: string;
  }) =>
    http.get<unknown, PaginatedResponse<TechCertificationVO>>('/service/tech-certifications', { params }),

  /** 获取认证目录（后台管理的 svc_cert_catalog） GET /api/service/cert-catalog */
  getCertCatalog: () =>
    http.get<unknown, CertCatalogVO[]>('/service/cert-catalog'),

  /** 新增技术认证 POST /api/service/tech-certifications */
  addTechCertification: (data: TechCertificationVO) =>
    http.post<unknown, boolean>('/service/tech-certifications', data),

  /** 获取能力矩阵列表（分页）GET /api/service/capability-matrix */
  getCapabilityMatrix: (params: {
    page?: number;
    pageSize?: number;
    filters?: Record<string, unknown>;
    keyword?: string;
  }) =>
    http.get<unknown, PaginatedResponse<CapabilityMatrixVO>>('/service/capability-matrix', { params }),

  /** 获取案例库列表（分页）GET /api/service/case-library */
  getCaseLibrary: (params: {
    page?: number;
    pageSize?: number;
    filters?: Record<string, unknown>;
    keyword?: string;
  }) =>
    http.get<unknown, PaginatedResponse<CaseLibraryVO>>('/service/case-library', { params }),

  /** 获取案例详情（触发浏览量+1）GET /api/service/case-library/{id} */
  getCaseLibraryDetail: (id: string) =>
    http.get<unknown, CaseLibraryVO>(`/service/case-library/${id}`),

  /** 新增案例 POST /api/service/case-library */
  addCase: (data: CaseLibraryVO) =>
    http.post<unknown, boolean>('/service/case-library', data),

  /** 获取工程师列表（分页）GET /api/service/engineers */
  getEngineers: (params: {
    page?: number;
    pageSize?: number;
    filters?: Record<string, unknown>;
    keyword?: string;
  }) =>
    http.get<unknown, PaginatedResponse<EngineerProfileVO>>('/service/engineers', { params }),

  /** 新增工程师 POST /api/service/engineers */
  addEngineer: (data: EngineerProfileVO) =>
    http.post<unknown, boolean>('/service/engineers', data),

  /** 获取合规认证列表（分页）GET /api/service/compliance */
  getComplianceCerts: (params: {
    page?: number;
    pageSize?: number;
    filters?: Record<string, unknown>;
    keyword?: string;
  }) =>
    http.get<unknown, PaginatedResponse<ComplianceCertVO>>('/service/compliance', { params }),

  /** 新增合规认证 POST /api/service/compliance */
  addComplianceCert: (data: ComplianceCertVO) =>
    http.post<unknown, boolean>('/service/compliance', data),

  // ==================== SLA标准 ====================

  /** 获取SLA套餐列表 GET /api/service/sla-standards */
  getSlaStandards: () =>
    http.get<unknown, SlaStandardVO[]>('/service/sla-standards'),

  /** 获取我购买的SLA订阅 GET /api/service/sla-subscriptions */
  getMySlaSubscriptions: () =>
    http.get<unknown, SlaSubscriptionVO[]>('/service/sla-subscriptions'),

  /** 查询我公司的 SLA 指标达成数据 GET /api/service/sla-metric/mine */
  getMySlaMetrics: () =>
    http.get<unknown, Array<{
      id: string; period: string; slaCode: string; metricName: string;
      promiseValue: number; actualValue: number; unit: string;
      achievementRate: number; status: 'met' | 'breached' | 'unknown';
      createTime: string;
    }>>('/service/sla-metric/mine'),

  /** 购买SLA套餐 POST /api/service/sla-subscriptions */
  purchaseSla: (data: { slaCode: string; months: number; isTrial?: boolean }) =>
    http.post<unknown, boolean>('/service/sla-subscriptions', data),

  // ==================== 质量审核 ====================

  /** 获取质量审核列表 GET /api/service/quality-audits */
  getQualityAudits: (params?: { page?: number; pageSize?: number }) =>
    http.get<unknown, PaginatedResponse<QualityAuditVO>>('/service/quality-audits', { params }),

  /** 获取质量审核详情（含问题明细）GET /api/service/quality-audits/{id} */
  getQualityAuditDetail: (id: string) =>
    http.get<unknown, QualityAuditVO>(`/service/quality-audits/${id}`),

  /** 提交问题反馈 POST /api/service/quality-audits/{auditId}/problems/{problemId}/feedback */
  submitQualityAuditFeedback: (auditId: string, problemId: string, feedback: string) =>
    http.post<unknown, boolean>(`/service/quality-audits/${auditId}/problems/${problemId}/feedback`, { feedback }),

  // ==================== 升级申请 ====================

  /** 获取升级条件 GET /api/service/upgrade/conditions */
  getUpgradeConditions: () =>
    http.get<unknown, UpgradeConditionsVO>('/service/upgrade/conditions'),

  /** 获取升级申请历史（不分页） GET /api/service/upgrade-applications/list */
  getUpgradeApplications: () =>
    http.get<unknown, UpgradeApplicationVO[]>('/service/upgrade-applications/list'),

  /** 提交升级申请 POST /api/service/upgrade-applications */
  submitUpgradeApplication: (data: { targetLevel: string; applyReason: string; attachmentIds?: string }) =>
    http.post<unknown, boolean>('/service/upgrade-applications', data),
};

// ============================================================
// 公共模块
// ============================================================

export interface RegionItem {
  name: string;
  cities: string[];
}

export const commonApi = {
  /** 获取全国省市区数据 GET /api/common/regions（无需认证） */
  getRegions: () =>
    http.get<unknown, RegionItem[]>('/common/regions'),
};

// ============================================================
// 公告与活动
// ============================================================

export const contentApi = {
  /** 获取政策公告列表 GET /api/announcements */
  getAnnouncements: () =>
    http.get<unknown, PolicyAnnouncement[]>('/announcements'),

  /** 获取市场活动列表 GET /api/activities */
  getActivities: () =>
    http.get<unknown, MarketActivity[]>('/activities'),
};

// ============================================================
// 认证申请模块（cert_application）
// ============================================================

export interface CertApplicationVO {
  id: string;
  partnerId: string;
  certId: string | null;
  category: string;
  name: string;
  issuer: string;
  obtainDate: string | null;
  validUntil: string | null;
  status: number; // 0=草稿 1=待审核 2=已驳回 3=已通过
  score: number | null;
  certNo: string | null;
  certificateUrl: string | null;
  auditComment: string | null;
  applyDate: string | null;
  remark: string | null;
  createTime: string;
}

export interface CertCategoryTree {
  id: string;
  name: string;
  parentId: string | null;
  sortOrder: number;
  children: CertCategoryTree[];
}

export interface CertCatalogItem {
  id: string;
  name: string;
  categoryId: string;
  categoryName: string;
  issuer: string;
  agentLevel: string;
  validMonths: number;
  status: string;
}

export interface CertStats {
  total: number;
  approved: number;
  pending: number;
  draft: number;
  rejected: number;
}

export const certApi = {
  /** 保存草稿 POST /api/cert/application/save */
  saveDraft: (data: { certId?: string; category: string; name: string; issuer: string; remark?: string }) =>
    http.post<unknown, CertApplicationVO>('/cert/application/save', data),

  /** 提交审核 POST /api/cert/application/submit */
  submit: (data: { id: string }) =>
    http.post<unknown, void>('/cert/application/submit', data),

  /** 列表 GET /api/cert/application/list */
  getList: () =>
    http.get<unknown, CertApplicationVO[]>('/cert/application/list'),

  /** 详情 GET /api/cert/application/detail */
  getDetail: (id: string) =>
    http.get<unknown, CertApplicationVO>('/cert/application/detail', { params: { id } }),

  /** 统计 GET /api/cert/application/stats */
  getStats: () =>
    http.get<unknown, CertStats>('/cert/application/stats'),

  /** 分类树 GET /api/cert/category/tree */
  getCategoryTree: () =>
    http.get<unknown, CertCategoryTree[]>('/cert/category/tree'),

  /** 目录列表 GET /api/cert/catalog/list */
  getCatalogList: (categoryId?: string) =>
    http.get<unknown, CertCatalogItem[]>('/cert/catalog/list', { params: categoryId ? { categoryId } : undefined }),
};

// ============================================================
// 导航树（前端静态配置，不走后端）
// ============================================================

export const navApi = {
  /** 获取导航树（根据模块返回对应导航数据） */
  getNavTree: (module?: string): Promise<NavTreeNode[]> => {
    const data: NavTreeNode[] = (() => {
      switch (module) {
        case 'projects':
          return projectsNavTree;
        case 'performance':
          return performanceNavTree;
        case 'resources':
          return resourcesNavTree;
        case 'quotation':
          return quotationNavTree;
        default:
          return navTreeMockData;
      }
    })();
    return Promise.resolve(data);
  },
};

// ============================================================
// 项目管理
// ============================================================

export interface OpportunityVO {
  id: string;
  title: string;
  customerName: string;
  opportunityType: string;
  estimatedAmount: number;
  probability: number;
  stage: string;
  createDate: string;
  expectedCloseDate?: string;
  region?: string;
  description?: string;
  /** 接手状态: AVAILABLE=可接手 CLAIMED=已接手 EXPIRED=已过期 */
  claimStatus?: string;
  /** 接手方合作伙伴ID */
  claimedPartnerId?: string;
  /** 接手方合作伙伴名称 */
  claimedPartnerName?: string;
  /** 接手时间 */
  claimedAt?: string;
  /** 接手过期时间 */
  claimExpireAt?: string;
  /** 是否由后台定向推荐 */
  recommended?: boolean;
  /** 推荐理由 */
  recommendReason?: string;
  /** 推荐优先级: 1-普通 2-重要 3-紧急 */
  recommendPriority?: number;
  /** 推荐发布时间 */
  recommendPublishedAt?: string;
}

export interface PaginatedData<T> {
  list: T[];
  total: number;
  page: number;
  pageSize: number;
}

export const projectApi = {
  /** 获取项目报备列表 GET /api/projects/registrations */
  getProjects: (params?: { status?: string; page?: number; pageSize?: number }) =>
    http.get<unknown, PaginatedData<unknown>>('/projects/registrations', { params }),

  /** 获取项目详情 GET /api/projects/registrations/{id} */
  getProjectById: (id: string) =>
    http.get<unknown, unknown>(`/projects/registrations/${id}`),

  /** 创建项目报备 POST /api/projects/registrations */
  createProject: (data: Record<string, unknown>) =>
    http.post<unknown, unknown>('/projects/registrations', data),

  /** 更新项目 PUT /api/projects/registrations/{id} */
  updateProject: (id: string, data: Record<string, unknown>) =>
    http.put<unknown, unknown>(`/projects/registrations/${id}`, data),

  /** 删除项目 DELETE /api/projects/registrations/{id} */
  deleteProject: (id: string) =>
    http.delete<unknown, boolean>(`/projects/registrations/${id}`),

  /** 提交报备 POST /api/projects/registrations/{id}/submit */
  submitProject: (id: string) =>
    http.post<unknown, unknown>(`/projects/registrations/${id}/submit`),

  /** 查询所有可接手机会（含后台推荐的） GET /api/projects/opportunities */
  getOpportunities: (params?: { page?: number; pageSize?: number }) =>
    http.get<unknown, PaginatedData<OpportunityVO>>('/projects/opportunities', { params }),

  /** 接手项目 POST /api/projects/opportunities/{id}/claim */
  claimOpportunity: (id: string) =>
    http.post<unknown, OpportunityVO>(`/projects/opportunities/${id}/claim`),

  /** 取消接手 POST /api/projects/opportunities/{id}/unclaim */
  unclaimOpportunity: (id: string) =>
    http.post<unknown, OpportunityVO>(`/projects/opportunities/${id}/unclaim`),

  /** 推进接手项目阶段 POST /api/projects/opportunities/{id}/advance-stage */
  advanceStage: (id: string, stage: string) =>
    http.post<unknown, OpportunityVO>(`/projects/opportunities/${id}/advance-stage`, { stage }),

  /** 放弃接手项目 POST /api/projects/opportunities/{id}/abandon */
  abandonOpportunity: (id: string) =>
    http.post<unknown, OpportunityVO>(`/projects/opportunities/${id}/abandon`),

  /** 查询我接手的项目 GET /api/projects/opportunities/my-claimed */
  getMyClaimedOpportunities: (params?: { page?: number; pageSize?: number }) =>
    http.get<unknown, PaginatedData<OpportunityVO>>('/projects/opportunities/my-claimed', { params }),

  /** 查询后台推荐给我的机会 GET /api/projects/opportunities/recommendations */
  getRecommendedOpportunities: (params?: { page?: number; pageSize?: number }) =>
    http.get<unknown, PaginatedData<OpportunityVO>>('/projects/opportunities/recommendations', { params }),

  /** 商机分析 GET /api/projects/opportunity-analysis */
  getOpportunityAnalysis: () =>
    http.get<unknown, Array<{ stage: string; count: number; totalAmount: number }>>('/projects/opportunity-analysis'),
};

// ============================================================
// 业绩与收益
// ============================================================

export const performanceApi = {
  /** 业绩总览 GET /api/performance/overview */
  getOverview: () =>
    http.get<unknown, unknown>('/performance/overview'),

  /** 业绩看板综合视图 GET /api/performance/overview-dashboard?year= */
  getOverviewDashboard: (year: number) =>
    http.get<unknown, unknown>('/performance/overview-dashboard', { params: { year } }),

  /** 佣金列表 GET /api/performance/commissions */
  getCommissions: (params?: { page?: number; pageSize?: number; status?: string }) =>
    http.get<unknown, unknown[]>('/performance/commissions', { params }),

  /** 奖励记录列表 GET /api/performance/rewards */
  getIncentives: (params?: { page?: number; pageSize?: number; status?: string }) =>
    http.get<unknown, unknown[]>('/performance/rewards', { params }),

  /** 奖励排行榜 GET /api/performance/reward-rankings */
  getRewardRankings: () =>
    http.get<unknown, unknown[]>('/performance/reward-rankings'),

  /** 奖励政策列表 GET /api/performance/reward-policies */
  getRewardPolicies: () =>
    http.get<unknown, unknown[]>('/performance/reward-policies'),

  /** 申请奖励 POST /api/performance/rewards/apply */
  applyReward: (policyId: string, applyReason: string) =>
    http.post<unknown, unknown>('/performance/rewards/apply', { policyId, applyReason }),

  /** 我的奖励申请列表 GET /api/performance/rewards/applications */
  getMyRewardApplications: () =>
    http.get<unknown, unknown[]>('/performance/rewards/applications'),

  /** 业绩分析 GET /api/performance/analysis?type= */
  getAnalysis: (type: string) =>
    http.get<unknown, unknown[]>('/performance/analysis', { params: { type } }),

  /** 收入趋势 GET /api/performance/trends */
  getTrends: () =>
    http.get<unknown, unknown[]>('/performance/trends'),

  /** 目标达成 GET /api/performance/target-achievement */
  getTargetAchievement: () =>
    http.get<unknown, unknown[]>('/performance/target-achievement'),

  /** 佣金明细 GET /api/performance/commission-details */
  getCommissionDetails: () =>
    http.get<unknown, unknown[]>('/performance/commission-details'),

  /** 佣金报表 GET /api/performance/commission-report */
  getCommissionReport: () =>
    http.get<unknown, unknown>('/performance/commission-report'),
};

// ============================================================
// 配置报价器
// ============================================================

export const quotationApi = {
  /** 获取产品列表 GET /api/products */
  getProducts: (params?: { category?: string; keyword?: string }) =>
    http.get<unknown, PaginatedResponse<ProductItem>>('/products', { params: { page: 1, pageSize: 100, ...(params || {}) } }),

  /** 获取报价单列表 GET /api/quotes */
  getQuotes: (params?: { status?: string; page?: number; pageSize?: number }) =>
    http.get<unknown, unknown[]>('/quotes', { params }),

  /** 获取服务器配置选项 GET /api/config/server */
  getServerConfig: () =>
    http.get<unknown, ServerConfigGroup[]>('/config/server'),

  /** 获取交换机配置选项 GET /api/config/switch */
  getSwitchConfig: () =>
    http.get<unknown, ServerConfigGroup[]>('/config/switch'),

  /** 获取安全设备配置选项 GET /api/config/security */
  getSecurityConfig: () =>
    http.get<unknown, ServerConfigGroup[]>('/config/security'),

  /** 获取软件产品配置选项 GET /api/config/software */
  getSoftwareConfig: () =>
    http.get<unknown, ServerConfigGroup[]>('/config/software'),

  /** 创建报价单 POST /api/quotes */
  createQuote: (data: Record<string, unknown>) =>
    http.post<unknown, unknown>('/quotes', data),

  /** 更新报价单 PUT /api/quotes/{id} */
  updateQuote: (id: string, data: Record<string, unknown>) =>
    http.put<unknown, unknown>(`/quotes/${id}`, data),

  /** 删除报价单（移入回收站）DELETE /api/quotes/{id} */
  deleteQuote: (id: string) =>
    http.delete<unknown, void>(`/quotes/${id}`),

  /** 提交草稿报价单 POST /api/quotes/{id}/submit（仅草稿可提交） */
  submitQuote: (id: string) =>
    http.post<unknown, void>(`/quotes/${id}/submit`),

  /** 彻底删除报价单（回收站物理删除）DELETE /api/quotes/{id}/permanent */
  deleteQuotePermanent: (id: string) =>
    http.delete<unknown, boolean>(`/quotes/${id}/permanent`),

  /** 取消订单 POST /api/quotes/{id}/cancel */
  cancelQuote: (id: string) =>
    http.post<unknown, void>(`/quotes/${id}/cancel`),

  /** 确认收货 POST /api/quotes/{id}/confirm */
  confirmQuote: (id: string) =>
    http.post<unknown, void>(`/quotes/${id}/confirm`),

  /** 申请售后 POST /api/quotes/{id}/after-sale */
  afterSaleQuote: (id: string) =>
    http.post<unknown, void>(`/quotes/${id}/after-sale`),

  /** 获取报价模板列表 GET /api/quote-templates */
  getTemplates: () =>
    http.get<unknown, unknown[]>('/quote-templates'),

  /** 把报价单保存为模板 POST /api/quotes/{id}/save-template */
  saveAsTemplate: (id: string) =>
    http.post<unknown, boolean>(`/quotes/${id}/save-template`),

  /** 获取导出记录 GET /api/export-records */
  getExportRecords: () =>
    http.get<unknown, unknown[]>('/export-records'),

  /** 获取导出模板列表 GET /api/export-templates */
  getExportTemplates: () =>
    http.get<unknown, unknown[]>('/export-templates'),

  /** 获取回收站列表 GET /api/quotes/trash */
  getTrashQuotes: () =>
    http.get<unknown, unknown[]>('/quotes/trash'),

  /** 恢复报价单 POST /api/quotes/{id}/restore */
  restoreQuote: (id: string) =>
    http.post<unknown, void>(`/quotes/${id}/restore`),

  /** 获取恢复记录 GET /api/quotes/restore-history */
  getRestoreHistory: () =>
    http.get<unknown, unknown[]>('/quotes/restore-history'),

  /** 获取共享报价 GET /api/quotes/shared */
  getSharedQuotes: () =>
    http.get<unknown, unknown[]>('/quotes/shared'),

  /** 从报价单导出 POST /api/quotes/export */
  exportQuote: (data: Record<string, unknown>) =>
    http.post<unknown, unknown>('/quotes/export', data),
};

// ============================================================
// 资源中心
// ============================================================

export const resourceApi = {
  /** 通用资源列表 GET /api/resources?type=xxx */
  getResources: (params?: { type?: string; category?: string }) =>
    http.get<unknown, unknown[]>('/resources', { params }),

  /** 产品手册 GET /api/resources?type=product-brochures */
  getProductBrochures: () =>
    http.get<unknown, unknown[]>('/resources', { params: { type: 'product-brochures' } }),

  /** 宣传视频 GET /api/resources?type=promotional-videos */
  getPromotionalVideos: () =>
    http.get<unknown, unknown[]>('/resources', { params: { type: 'promotional-videos' } }),

  /** 海报图片 GET /api/resources?type=posters */
  getPosters: () =>
    http.get<unknown, unknown[]>('/resources', { params: { type: 'posters' } }),

  /** 方案白皮书 GET /api/resources?type=whitepapers */
  getWhitepapers: () =>
    http.get<unknown, unknown[]>('/resources', { params: { type: 'whitepapers' } }),

  /** 技术规格 GET /api/resources?type=tech-specs */
  getTechSpecs: () =>
    http.get<unknown, unknown[]>('/resources', { params: { type: 'tech-specs' } }),

  /** 接口文档 GET /api/resources?type=api-docs */
  getApiDocs: () =>
    http.get<unknown, unknown[]>('/resources', { params: { type: 'api-docs' } }),

  /** 合同范本 GET /api/resources?type=contract-templates */
  getContractTemplates: () =>
    http.get<unknown, unknown[]>('/resources', { params: { type: 'contract-templates' } }),

  /** 协议模板 GET /api/resources?type=agreement-templates */
  getAgreementTemplates: () =>
    http.get<unknown, unknown[]>('/resources', { params: { type: 'agreement-templates' } }),

  /** 法律文件 GET /api/resources?type=legal-docs */
  getLegalDocs: () =>
    http.get<unknown, unknown[]>('/resources', { params: { type: 'legal-docs' } }),

  /** 成功案例 GET /api/resources/success-cases */
  getSuccessCases: () =>
    http.get<unknown, unknown[]>('/resources/success-cases'),

  /** 培训课程 GET /api/resources/training-courses?courseType=xxx */
  getTrainingCourses: (courseType?: string) =>
    http.get<unknown, unknown[]>('/resources/training-courses', { params: { courseType } }),

  /**
   * 资源下载直链 GET /api/resources/{id}/download
   * 后端下载次数 +1 后 302 到真实文件流；供 <a href> 直接使用（不带 token，后端已放行鉴权）
   */
  downloadHref: (id: string) => `/api/resources/${id}/download`,
};

// ============================================================
// 健康度
// ============================================================

export interface HealthHistoryVO {
  id: string;
  partnerId: string;
  snapshotDate: string;
  totalScore: number;
  dimensionScores?: string;
  triggerEvent?: string;
  createTime?: string;
}

export const healthApi = {
  /** 获取我的健康度评分 GET /api/health/my-score */
  getMyScore: () =>
    http.get<unknown, HealthScoreVO | null>('/health/my-score'),

  /** 立即重新计算我的健康度评分 POST /api/health/recalculate */
  recalculate: () =>
    http.post<unknown, HealthScoreVO>('/health/recalculate'),

  /** 获取最近 N 天健康度历史快照 GET /api/health/history?days= */
  getHistory: (days: number = 30) =>
    http.get<unknown, HealthHistoryVO[]>('/health/history', { params: { days } }),
};

// ============================================================
// 服务星级 / 服务等级
// ============================================================

/** 服务等级配置 */
export interface ServiceLevelVO {
  id: string;
  agentLevel: string;
  levelName: string;
  serviceStars: number;
  threshold: number;
  supportResponseHours: number;
  dedicatedManager: boolean;
  requiredEngineers: number;
  benefits: string[];
  description: string;
}

/** 服务星级总览 */
export interface ServiceLevelOverviewVO {
  levels: ServiceLevelVO[];
  currentStar: number;
  currentLevel: ServiceLevelVO | null;
  yearAmount: number;
  year: number;
  nextLevel: ServiceLevelVO | null;
  gapAmount: number;
  progressPct: number;
}

export const serviceLevelApi = {
  /** 服务星级总览 GET /api/service-level/overview */
  getOverview: () =>
    http.get<unknown, ServiceLevelOverviewVO>('/service-level/overview'),
};

// ============================================================
// MDF市场发展基金
// ============================================================

export const mdfApi = {
  /** 获取我的MDF申请列表 GET /api/mdf/my-requests */
  getMyRequests: (params?: { page?: number; pageSize?: number }) =>
    http.get<unknown, PaginatedResponse<MdfRequestVO>>('/mdf/my-requests', { params }),

  /** 提交MDF申请 POST /api/mdf/submit */
  submitRequest: (data: Partial<MdfRequestVO>) =>
    http.post<unknown, boolean>('/mdf/submit', data),
};

/**
 * 兼容旧 mockApi 调用的统一导出
 * partnerStore 和 NavTree 可直接使用此对象
 */
export const api = {
  ...partnerApi,
  ...contentApi,
  getNavTree: navApi.getNavTree,
};
